import React, { useState, useMemo } from 'react';
import {
    ChevronDownIcon,
    ChevronRightIcon,

    MapPinIcon,
    ShipIcon,
    TruckIcon,
    ContainerIcon,

    DatabaseIcon,
} from 'lucide-react';
import JsonView from '@uiw/react-json-view';
import Sidebar from '@/components/sidebar';
import TrackingControls from './tracking';

interface DebugShipmentDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    selectedRow: any[] | null;
    trackingJob: {
        data?: {
            id?: string | null;
            status?: string;
        } | null;
    };
    shipmentEvents: {
        data?: any;
    };
    shipmentData?: any;
    columns?: any[];
}

interface ProcessedEvent {
    id: string;
    type: 'departure' | 'arrival';
    title: string;
    datetime: string;
    classification: 'EST' | 'ACT' | 'PLN';
    location?: string;
    vessel?: string;
    description: string;
    originalEvent: any;
    eventCode?: string;
    eventType?: string;
}

interface DepartureArrivalData {
    estimated_etd?: { datetime: string; event?: any; source: 'shipment' | 'event' };
    actual_etd?: { datetime: string; event?: any; source: 'shipment' | 'event' };
    estimated_eta?: { datetime: string; event?: any; source: 'shipment' | 'event' };
    actual_eta?: { datetime: string; event?: any; source: 'shipment' | 'event' };
}

const DebugShipmentDrawer: React.FC<DebugShipmentDrawerProps> = ({
    isOpen,
    onClose,
    selectedRow,
    trackingJob,
    shipmentEvents,
    shipmentData
}) => {
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
    const [expandedEventDetails, setExpandedEventDetails] = useState<Set<string>>(new Set());


    const getReferenceType = (shipment: any) => {
        if (shipment?.customFields?.container) return 'container';
        if (shipment?.customFields?.bookingNumber) return 'booking';
        if (shipment?.customFields?.bolNumber) return 'bol';
        return null;
    };

    const getReferenceNumber = (shipment: any) => {
        if (shipment?.customFields?.container) return shipment?.customFields?.container;
        if (shipment?.customFields?.bookingNumber) return shipment?.customFields?.bookingNumber;
        if (shipment?.customFields?.bolNumber) return shipment?.customFields?.bolNumber;

        return null;
    };

    // Process events to find only departure and arrival events
    const departureArrivalEvents = useMemo((): ProcessedEvent[] => {
        if (!shipmentEvents || !Array.isArray(shipmentEvents)) return [];

        return shipmentEvents
            .filter((event: any) => {
                // Handle both field naming conventions
                const eventType = event.eventType || event.type;
                const eventCode = event.eventCode;
                const isDeparture = eventType === 'TRANSPORT' && eventCode === 'DEPA';
                const isArrival = eventType === 'TRANSPORT' && eventCode === 'ARRI';
                return isDeparture || isArrival;
            })
            .map((event: any, index: number): ProcessedEvent => {
                const eventType = event.eventType || event.type;
                const eventCode = event.eventCode;
                const isDeparture = eventType === 'TRANSPORT' && eventCode === 'DEPA';

                return {
                    id: event.id || `event-${index}`,
                    type: isDeparture ? 'departure' : 'arrival',
                    title: isDeparture ? 'Departure' : 'Arrival',
                    datetime: event.eventDatetime || event.datetime || new Date().toISOString(),
                    classification: event.eventClassification || event.classifier || 'ACT',
                    location: event.locationData?.locationName || event.location?.locationName || event.locationData?.unLocode || event.location?.unLocationCode,
                    vessel: event.vesselData?.vesselName || event.vessel?.vesselName,
                    description: event.description || '',
                    originalEvent: event,
                    eventCode: eventCode,
                    eventType: eventType
                };
            })
            .sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime());
    }, [shipmentEvents]);


    const toggleSection = (sectionId: string) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(sectionId)) {
            newExpanded.delete(sectionId);
        } else {
            newExpanded.add(sectionId);
        }
        setExpandedSections(newExpanded);
    };

    const toggleEventDetails = (eventId: string) => {
        const newExpanded = new Set(expandedEventDetails);
        if (newExpanded.has(eventId)) {
            newExpanded.delete(eventId);
        } else {
            newExpanded.add(eventId);
        }
        setExpandedEventDetails(newExpanded);
    };

    return (
        <Sidebar isOpen={isOpen} onClose={onClose}>
            <div className="space-y-4">
                {/* Shipment Overview */}
                {shipmentData && (
                    <ExpandableSection
                        id="shipment-overview"
                        title="Shipment Overview"
                        icon={<ContainerIcon className="w-4 h-4" />}
                        isExpanded={expandedSections.has('shipment-overview')}
                        onToggle={() => toggleSection('shipment-overview')}
                    >
                        <div className="space-y-2 text-sm">
                            <div><strong>Container:</strong> {shipmentData.seaReferences?.containers?.[0] || 'N/A'}</div>
                            <div><strong>Booking:</strong> {shipmentData.seaReferences?.bookingReference || 'N/A'}</div>
                            <div><strong>Carrier:</strong> {shipmentData.parties?.find((p: any) => p.role === 'carrier')?.name || 'N/A'}</div>
                            <div><strong>Vessel:</strong> {shipmentData.customFields?.nazwa_statku || 'N/A'}</div>
                            <div><strong>Route:</strong> {shipmentData.originLocation} → {shipmentData.destinationLocation}</div>
                            <div><strong>Status:</strong>
                                <span className={`ml-2 px-2 py-1 rounded text-xs ${getStatusColor(shipmentData.status)}`}>
                                    {shipmentData.status}
                                </span>
                            </div>
                        </div>
                    </ExpandableSection>
                )}

                {/* Departure & Arrival Events */}
                <ExpandableSection
                    id="departure-arrival-events"
                    title="Departure & Arrival Events"
                    icon={<ShipIcon className="w-4 h-4" />}
                    count={departureArrivalEvents.length}
                    isExpanded={expandedSections.has('departure-arrival-events')}
                    onToggle={() => toggleSection('departure-arrival-events')}
                >
                    <div className="space-y-2">
                        {departureArrivalEvents.length === 0 ? (
                            <div className="text-sm text-gray-500">No departure/arrival events found</div>
                        ) : (
                            departureArrivalEvents.map((event) => (
                                <div key={event.id}>
                                    <EventCard
                                        event={event}
                                        onClick={() => toggleEventDetails(event.id)}
                                    />
                                    {expandedEventDetails.has(event.id) && (
                                        <div className="mt-2 ml-4 p-3 bg-gray-50 rounded border-l-2 border-gray-300">
                                            <JsonView
                                                value={event.originalEvent}
                                                displayDataTypes={false}
                                                collapsed={2}
                                                style={{ backgroundColor: 'transparent' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </ExpandableSection>

                {/* Raw Events Data */}
                <ExpandableSection
                    id="raw-events"
                    title="Raw Events Data"
                    icon={<DatabaseIcon className="w-4 h-4" />}
                    count={shipmentEvents?.length || 0}
                    isExpanded={expandedSections.has('raw-events')}
                    onToggle={() => toggleSection('raw-events')}
                >
                    {shipmentEvents ? (
                        <JsonView
                            value={shipmentEvents}
                            displayDataTypes={false}
                            collapsed={3}
                            style={{ backgroundColor: '#f5f5f5' }}
                        />
                    ) : (
                        <div className="text-sm text-gray-500">No events data available</div>
                    )}
                </ExpandableSection>

                {/* Row Data */}
                <ExpandableSection
                    id="row-data"
                    title="Row Data"
                    icon={<ContainerIcon className="w-4 h-4" />}
                    isExpanded={expandedSections.has('row-data')}
                    onToggle={() => toggleSection('row-data')}
                >
                    {selectedRow ? (
                        <JsonView
                            value={selectedRow}
                            displayDataTypes={false}
                            collapsed={2}
                            style={{ backgroundColor: '#f5f5f5' }}
                        />
                    ) : (
                        <div className="text-sm text-gray-500">No row selected</div>
                    )}
                </ExpandableSection>

                {/* Shipment Full Data */}
                {shipmentData && (
                    <ExpandableSection
                        id="shipment-data"
                        title="Shipment Data"
                        icon={<ShipIcon className="w-4 h-4" />}
                        isExpanded={expandedSections.has('shipment-data')}
                        onToggle={() => toggleSection('shipment-data')}
                    >
                        <JsonView
                            value={shipmentData}
                            displayDataTypes={false}
                            collapsed={2}
                            style={{ backgroundColor: '#f5f5f5' }}
                        />
                    </ExpandableSection>
                )}
                {/* Tracking Job */}
                <ExpandableSection
                    id="tracking-job"
                    title="Tracking Job"
                    icon={<TruckIcon className="w-4 h-4" />}
                    isExpanded={expandedSections.has('tracking-job')}
                    onToggle={() => toggleSection('tracking-job')}
                >
                    <>
                        {selectedRow ? (
                            <TrackingControls
                                shipmentId={shipmentData?.id}
                                carrierName={shipmentData?.customFields?.carrier}
                                referenceType={getReferenceType(shipmentData)}
                                referenceNumber={getReferenceNumber(shipmentData)}
                                jobId={trackingJob?.id || null}
                                isJobPaused={trackingJob?.status === 'paused' || false}
                            />
                        ) : (
                            <div className="text-sm text-gray-500">No row selected</div>
                        )}
                        {trackingJob ? (
                            <JsonView
                                value={trackingJob}
                                displayDataTypes={false}
                                collapsed={2}
                                style={{ backgroundColor: '#f5f5f5' }}
                            />
                        ) : (
                            <div className="text-sm text-gray-500">No tracking job data</div>
                        )}</>
                </ExpandableSection>
            </div>
        </Sidebar>
    );
};

// Helper Components
interface ExpandableSectionProps {
    id: string;
    title: string;
    icon?: React.ReactNode;
    count?: number;
    isExpanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}

const ExpandableSection: React.FC<ExpandableSectionProps> = ({
    title,
    icon,
    count,
    isExpanded,
    onToggle,
    children
}) => (
    <div className="border border-gray-200 rounded-lg">
        <button
            className="w-full p-3 flex items-center justify-between text-left hover:bg-gray-50"
            onClick={onToggle}
        >
            <div className="flex items-center space-x-2">
                {icon}
                <span className="font-medium text-gray-800">{title}</span>
                {count !== undefined && (
                    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                        {count}
                    </span>
                )}
            </div>
            {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4 text-gray-500" />
            ) : (
                <ChevronRightIcon className="w-4 h-4 text-gray-500" />
            )}
        </button>
        {isExpanded && (
            <div className="p-3 pt-0 border-t border-gray-100">
                {children}
            </div>
        )}
    </div>
);

interface DateTimeCardProps {
    label: string;
    data: { datetime: string; event?: any; source: 'shipment' | 'event' };
    onEventClick?: () => void;
    isEventExpanded?: boolean;
}

const DateTimeCard: React.FC<DateTimeCardProps> = ({ label, data, onEventClick, isEventExpanded }) => (
    <div className="bg-white border rounded p-3">
        <div className="flex items-center justify-between">
            <div>
                <div className="text-sm font-medium text-gray-900">{label}</div>
                <div className="text-sm text-gray-600">{formatDateTime(data.datetime)}</div>
                <div className="text-xs text-gray-500">
                    Source: {data.source === 'shipment' ? 'Shipment Data' : 'Event Data'}
                </div>
            </div>
            {data.event && onEventClick && (
                <button
                    onClick={onEventClick}
                    className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded hover:bg-blue-200"
                >
                    {isEventExpanded ? 'Hide Event' : 'Show Event'}
                </button>
            )}
        </div>
        {data.event && isEventExpanded && (
            <div className="mt-3 pt-3 border-t border-gray-200">
                <JsonView
                    value={data.event}
                    displayDataTypes={false}
                    collapsed={2}
                    style={{ backgroundColor: '#f9fafb' }}
                />
            </div>
        )}
    </div>
);

interface EventCardProps {
    event: ProcessedEvent;
    onClick: () => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, onClick }) => (
    <div
        className="p-3 border border-gray-200 rounded hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={onClick}
    >
        <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
                {event.type === 'departure' ? (
                    <ShipIcon className="w-4 h-4 text-blue-600" />
                ) : (
                    <MapPinIcon className="w-4 h-4 text-green-600" />
                )}
                <span className="font-medium text-sm">{event.title}</span>
                <span className={`px-2 py-1 rounded text-xs ${getClassificationColor(event.classification)}`}>
                    {event.classification}
                </span>
            </div>
            <ChevronRightIcon className="w-4 h-4 text-gray-400" />
        </div>
        <div className="mt-1 text-xs text-gray-600">
            {formatDateTime(event.datetime)}
        </div>
        {event.location && (
            <div className="text-xs text-gray-500 flex items-center mt-1">
                <MapPinIcon className="w-3 h-3 mr-1" />
                {event.location}
            </div>
        )}
    </div>
);

// Helper Functions
function getClassificationColor(classification: string): string {
    switch (classification) {
        case 'ACT': return 'bg-green-100 text-green-800';
        case 'EST': return 'bg-blue-100 text-blue-800';
        case 'PLN': return 'bg-gray-100 text-gray-800';
        default: return 'bg-gray-100 text-gray-800';
    }
}

function getStatusColor(status: string): string {
    switch (status) {
        case 'DEPARTED': return 'bg-blue-100 text-blue-800';
        case 'ARRIVED': case 'DELIVERED': return 'bg-green-100 text-green-800';
        case 'IN_TRANSIT': return 'bg-yellow-100 text-yellow-800';
        case 'FAILED': return 'bg-red-100 text-red-800';
        default: return 'bg-gray-100 text-gray-800';
    }
}

function formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString();
}

export default DebugShipmentDrawer;